import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Loader, EmptyState } from "@/components/common";
import { PageTitle, Panel } from "./AdminShell";
import { ScrollText, FileText, Trash2, Star, Download } from "lucide-react";

const ICON = (action) => {
  if (action.includes("Devis")) return FileText;
  if (action.includes("supprim")) return Trash2;
  if (action.includes("Avis")) return Star;
  return ScrollText;
};

export default function AdminActivityLog() {
  const [entries, setEntries] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const load = useCallback(() => {
    api.get("/admin/activity-log").then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/reminder-stats", { params: { period: statsPeriod } }).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, [statsPeriod]);

  const actions = useMemo(() => [...new Set((entries || []).map((e) => e.action))], [entries]);
  const users = useMemo(() => [...new Set((entries || []).map((e) => e.user_name).filter(Boolean))], [entries]);

  if (!entries) return <Loader />;

  const list = entries.filter((e) => (actionFilter === "all" || e.action === actionFilter) && (userFilter === "all" || e.user_name === userFilter));

  const exportCsv = () => {
    const rows = [["Date", "Action", "Détail", "Auteur", "Rôle"],
      ...list.map((e) => [e.created_at, e.action, e.detail || "", e.user_name || "", e.role || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "journal-activite.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <PageTitle>Journal d'activité</PageTitle>
      {stats && (
        <div className="mb-4" data-testid="reminder-stats">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Impact des relances</span>
            <select value={statsPeriod} onChange={(e) => setStatsPeriod(e.target.value)} className="bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs" data-testid="stats-period">
              <option value="all">Depuis le début</option>
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Panel className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Relances panier</p>
              <p className="text-2xl font-display font-bold text-slate-800 mt-1">{stats.cart_converted}<span className="text-base text-slate-400 font-num"> / {stats.cart_sent}</span></p>
              <p className="text-xs text-slate-500">abouties à une commande{stats.cart_sent > 0 ? ` · ${Math.round((stats.cart_converted / stats.cart_sent) * 100)}% de conversion` : ""}</p>
            </Panel>
            <Panel className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Relances devis</p>
              <p className="text-2xl font-display font-bold text-slate-800 mt-1 font-num">{stats.quote_sent}</p>
              <p className="text-xs text-slate-500">relance(s) de devis envoyée(s)</p>
            </Panel>
          </div>
        </div>
      )}
      {entries.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="journal-filter-action">
            <option value="all">Toutes les actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="journal-filter-user">
            <option value="all">Toute l'équipe</option>
            {users.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <button onClick={exportCsv} className="ml-auto inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-bold px-3 py-2 rounded-lg hover:bg-slate-800" data-testid="journal-export"><Download className="w-4 h-4" /> Exporter CSV</button>
        </div>
      )}
      {list.length === 0 ? (
        <EmptyState icon={ScrollText} title="Aucune activité" subtitle="Les actions sensibles (devis générés, avis supprimés…) apparaîtront ici." />
      ) : (
        <Panel className="divide-y divide-slate-100">
          {list.map((e) => {
            const Icon = ICON(e.action);
            return (
              <div key={e.id} className="flex items-start gap-3 px-4 py-3" data-testid={`activity-${e.id}`}>
                <div className="w-8 h-8 rounded-lg bg-[#FDF3EE] flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#C85A32]" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800">{e.action}</p>
                  {e.detail && <p className="text-xs text-slate-500">{e.detail}</p>}
                  <p className="text-[11px] text-slate-400 mt-0.5">{e.user_name || "—"}{e.role ? ` (${e.role})` : ""} · {formatDateTime(e.created_at)}</p>
                </div>
              </div>
            );
          })}
        </Panel>
      )}
    </div>
  );
}
